import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar
} from 'lucide-react';
import { leavesApi, LeaveType } from '../../services/api/leaves';
import toast from 'react-hot-toast';

interface LeaveTypesManagementProps {
  className?: string;
}

interface LeaveTypeFormData {
  name: string;
  description: string;
  maxDaysPerYear: number;
  carryForward: boolean;
  maxCarryForwardDays: number;
  requiresApproval: boolean;
  color: string;
}

const defaultFormData: LeaveTypeFormData = {
  name: '',
  description: '',
  maxDaysPerYear: 20,
  carryForward: false,
  maxCarryForwardDays: 0,
  requiresApproval: true,
  color: '#3B82F6'
};

const predefinedColors = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
];

const LeaveTypesManagement: React.FC<LeaveTypesManagementProps> = ({ className = '' }) => {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null);
  const [formData, setFormData] = useState<LeaveTypeFormData>(defaultFormData);

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      const response = await leavesApi.getLeaveTypes();
      setLeaveTypes(response || []);
    } catch (error: any) {
      console.error('Error fetching leave types:', error);
      toast.error('Failed to load leave types');
      setLeaveTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Leave type name is required');
      return;
    }

    if (formData.maxDaysPerYear <= 0) {
      toast.error('Maximum days per year must be greater than 0');
      return;
    }

    if (formData.carryForward && formData.maxCarryForwardDays <= 0) {
      toast.error('Maximum carry forward days must be specified when carry forward is enabled');
      return;
    }

    try {
      setSaving(true);

      if (editingLeaveType) {
        // Update existing leave type
        await leavesApi.updateLeaveType(editingLeaveType._id, formData);
        toast.success('Leave type updated successfully');
      } else {
        // Create new leave type
        await leavesApi.createLeaveType(formData);
        toast.success('Leave type created successfully');
      }

      setShowForm(false);
      setEditingLeaveType(null);
      setFormData(defaultFormData);
      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error saving leave type:', error);
      toast.error(error.response?.data?.message || 'Failed to save leave type');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (leaveType: LeaveType) => {
    setEditingLeaveType(leaveType);
    setFormData({
      name: leaveType.name,
      description: leaveType.description || '',
      maxDaysPerYear: leaveType.maxDaysPerYear,
      carryForward: leaveType.carryForward,
      maxCarryForwardDays: leaveType.maxCarryForwardDays || 0,
      requiresApproval: leaveType.requiresApproval,
      color: leaveType.color
    });
    setShowForm(true);
  };

  const handleDelete = async (leaveType: LeaveType) => {
    if (!confirm(`Are you sure you want to delete "${leaveType.name}"?`)) {
      return;
    }

    try {
      await leavesApi.deleteLeaveType(leaveType._id);
      toast.success('Leave type deleted successfully');
      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error deleting leave type:', error);
      toast.error(error.response?.data?.message || 'Failed to delete leave type');
    }
  };

  const createDefaultLeaveTypes = async () => {
    try {
      setSaving(true);
      const response = await leavesApi.createDefaultLeaveTypes();

      if (response && response.length > 0) {
        toast.success(`${response.length} default leave types created successfully`);
      } else {
        toast.info('Default leave types already exist');
      }

      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error creating default leave types:', error);

      if (error.response?.status === 403) {
        toast.error('Only firm admin can create default leave types');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create default leave types');
      }
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingLeaveType(null);
    setShowForm(false);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Leave Types Management</span>
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure leave types available for employees to apply
          </p>
        </div>
        <div className="flex space-x-2">
          {leaveTypes.length === 0 && (
            <button
              onClick={createDefaultLeaveTypes}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Settings className="h-4 w-4 mr-2" />
                  Create Default Leave Types
                </>
              )}
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Leave Type
          </button>
        </div>
      </div>

      {/* Leave Types List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading leave types...</span>
        </div>
      ) : leaveTypes.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Leave Types Found</h3>
          <p className="text-gray-600 mb-4">
            Get started by creating default leave types for your organization
          </p>
          <button
            onClick={createDefaultLeaveTypes}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center mx-auto"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Default Leave Types...
              </>
            ) : (
              <>
                <Settings className="h-4 w-4 mr-2" />
                Create Default Leave Types
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Leave Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Days/Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Carry Forward
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Approval Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaveTypes.map((leaveType) => (
                  <tr key={leaveType._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full mr-3"
                          style={{ backgroundColor: leaveType.color }}
                        ></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {leaveType.name}
                          </div>
                          {leaveType.description && (
                            <div className="text-sm text-gray-500">
                              {leaveType.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {leaveType.maxDaysPerYear} days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {leaveType.carryForward ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <X className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className="text-sm text-gray-900">
                          {leaveType.carryForward 
                            ? `Yes (${leaveType.maxCarryForwardDays || 0} days)` 
                            : 'No'
                          }
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {leaveType.requiresApproval ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <X className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className="text-sm text-gray-900">
                          {leaveType.requiresApproval ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(leaveType)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Edit leave type"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(leaveType)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete leave type"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Leave Type Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingLeaveType ? 'Edit Leave Type' : 'Create Leave Type'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Type Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Annual Leave"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional description"
                  rows={2}
                />
              </div>

              {/* Max Days Per Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Days Per Year *
                </label>
                <input
                  type="number"
                  value={formData.maxDaysPerYear}
                  onChange={(e) => setFormData({ ...formData, maxDaysPerYear: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="365"
                  required
                />
              </div>

              {/* Carry Forward */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.carryForward}
                    onChange={(e) => setFormData({
                      ...formData,
                      carryForward: e.target.checked,
                      maxCarryForwardDays: e.target.checked ? formData.maxCarryForwardDays : 0
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Allow Carry Forward</span>
                </label>
              </div>

              {/* Max Carry Forward Days */}
              {formData.carryForward && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Carry Forward Days *
                  </label>
                  <input
                    type="number"
                    value={formData.maxCarryForwardDays}
                    onChange={(e) => setFormData({ ...formData, maxCarryForwardDays: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    required
                  />
                </div>
              )}

              {/* Requires Approval */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Requires Approval</span>
                </label>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex space-x-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        formData.color === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingLeaveType ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypesManagement;
